import { disableKosten } from '../../helpers/showKosten';
import formStyle from '../../core/forms/style/WForm.less';
import { i18n } from '../../i18n/i18n';
import { Image, Text, View } from '@react-pdf/renderer';
import { parsePdfSub2 } from './generisch/helper';
import * as React from 'react';
import { StackedDiagram } from './helper/stackedBarChart';
import { svgToPng } from '../helper';
import { WHelpIcon } from '../../core/elements/WHelpIcon';

interface EnergieCockpitVisualisierungProps extends React.ClassAttributes<any> {
    data: any;
    dataItems: any[];
    jahr: number;
    onPdfDataLoaded?: (data: JSX.Element) => void;
}

interface EnergieCockpitVisualisierungState {
    isReady: boolean;
    selectedData: string;
}

export class EnergieCockpitVisualisierung extends React.Component<EnergieCockpitVisualisierungProps, EnergieCockpitVisualisierungState> {

    constructor(props: any) {
        super(props);
        this.state = {
            isReady: false,
            selectedData: 'v'
        };
        this.processPdfData = this.processPdfData.bind(this);
    }

    private disableKosten: boolean;

    componentDidMount(): void {
        this.disableKosten = disableKosten(this.props.data);

        if (typeof this.props.onPdfDataLoaded === 'function') {
            this.processPdfData();
        }
    }

    componentDidUpdate(prevProps: any, prevState: any): void {
        if (
            (this.state.isReady !== prevState.isReady && typeof this.props.onPdfDataLoaded === 'function')
            || this.props.dataItems !== prevProps.dataItems
            || this.state.selectedData !== prevState.selectedData
        ) {
            if (this.props.dataItems !== prevProps.dataItems || this.state.selectedData !== prevState.selectedData) {
                this.setState({ isReady: false }, this.processPdfData);
            } else {
                this.processPdfData();
            }
        }
    }

    getAxisLabel(): string {
        switch (this.state.selectedData || null) {
            case 'c': return 'CO₂ [t]';
            case 'k': return `${i18n('Kosten', 'sys')} [€]`;
            case 'v': return `${i18n('Verbrauch', 'sys')} [kWh]`;
            default: return '???';
        }
    }

    getBars(): any[] {
        return [
            { name: i18n('Strom', 'sys'), color: '#606468' },
            { name: i18n('Wärme', 'sys'), color: '#6d893f' },
            { name: i18n('Kraftstoffe', 'sys'), color: '#c5ceb9' }
        ];
    }

    getData(): any[] {
        const dataItems = this.props.dataItems.filter((item: any, index: number) => index < 3); // "Gesamt" wegfiltern
        const jahr: number = this.props.jahr || null;
        const sel = this.state.selectedData || null;
        const values_jahr = dataItems.map((item: any) => item[`${sel}_jahr`] || 0);
        const values_vorjahr = dataItems.map((item: any) => item[`${sel}_vorjahr`] || 0);

        return [
            { name: jahr, values: values_jahr },
            { name: jahr ? +jahr - 1 : null, values: values_vorjahr }
        ];
    }

    getPdfData(images: any[]): void {
        const energiedaten: any = (images || []).find((item: any) => (item || {}).id === `energiedaten-${this.props.jahr ||''}`) || {};
        this.props.onPdfDataLoaded(
            <View style={{ marginLeft: 10, border: '1 solid #e9e9e9', flex: 1 }}>
                <View
                    style={{
                        backgroundColor: '#e9e9e9', padding: 2, display: 'flex', flex: 1, flexDirection: 'row',
                        justifyContent: 'space-between', alignItems: 'center'
                    }}
                >
                    <View style={{ color: 'black', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: '10pt' }}>{i18n('Energiedaten', 'sys')}</Text>
                        <Text style={{ fontSize: '8pt', marginLeft: 2 }}>- {i18n('grafisch', 'sys')}</Text>
                    </View>
                    <View style={{ fontSize: '9pt', color: '#555' }}>
                        <Text>{parsePdfSub2(this.getTitel(), { fontSize: '9pt' })}</Text>
                    </View>
                </View>
                <View style={{ margin: 'auto', marginTop: 0, marginBottom: 0 }}>
                    {energiedaten.image || null}
                    <View style={{ position: 'absolute', top: '33%', right: 5 }}>
                        {energiedaten.legend || null}
                    </View>
                </View>
            </View>
        );
    }

    getStyle(isPdf?: boolean): any {
        return ({
            legend: {
                marginRight: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
            },
            legend_icon: {
                width: 6,
                height: 6,
                marginRight: 4,
                marginLeft: 4
            },
            legend_text: {
                fontSize: '8pt',
                color: '#555',
                marginRight: 4,
                display: 'flex',
                flexDirection: 'row'
            },
            pdf: {
                fontFamily: 'Calibri',
                fontSize: '9pt'
            }
        });
    }

    getTitel(): string {
        switch (this.state.selectedData || null) {
            case 'c': return i18n('CO₂-Emission', 'sys');
            case 'k': return i18n('Energiekosten', 'sys');
            case 'v': return i18n('Energieverbrauch', 'sys');
            default: return '???';
        }
    }

    async processPdfData(): Promise<void> {
        const charts: any[] = await [];
        const data: any[] = await [];
        const recharts: NodeListOf<HTMLElement> = document.querySelectorAll('.recharts-responsive-container');
        const styles: any = this.getStyle(true);

        for (let index = 0, length: number = recharts.length; index < length; ++index) {
            const chart: HTMLElement = recharts[index].querySelector('.recharts-surface');
            const id: string = recharts[index].id || null;
            const legend: NodeListOf<HTMLElement> = recharts[index].querySelectorAll('.recharts-legend-wrapper li');
            const legendItems: any[] = Array.from(legend)
                .map((li: any) => {
                    const color = `#${
                        ((li.firstChild.style.backgroundColor || '').substr(4).split(')')[0].split(', '))
                            .map((rgb: number) => (+rgb).toString(16))
                            .join('')
                    }`;
                    return { data: li.lastChild.data, color };
                });

            if (!chart) {
                continue;
            }
            charts.push({
                ...(id ? { id } : {}),
                html: chart,
                legend: legendItems || []
            });
        }

        charts.forEach(async (item: any): Promise<void> => {
            const html: HTMLElement = item.html || <svg />;
            html.style.fontFamily = styles.pdf.fontFamily || null;
            html.style.fontSize = styles.pdf.fontSize || null;
            const id: string = item.id || null;
            const legend: any = item.legend || [];
            const rect = html.getBoundingClientRect();
            const svg = new XMLSerializer().serializeToString(html);
            const svgBlob = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
            const image: any = await svgToPng(svgBlob, rect.width, rect.height, 3);
            data.push({
                ...(id ? { id } : {}),
                image: <Image src={image} style={{ maxWidth: rect.width * 0.75 }} />,
                legend: legend ? this.renderPdfLegend(legend) : null,
                maxWidth: rect.width * 0.75
            });

            if (data.length === charts.length) {
                this.setState({ isReady: true }, () => this.getPdfData(data));
            }
        });
    }

    renderPdfLegend(data: any) {
        const styles: any = this.getStyle(true);

        return data.map((item: any, index: number) =>
            <View key={`legend-${index}`} style={styles.legend}>
                {item.color && <Text style={{ ...styles.legend_icon, backgroundColor: item.color }} />}
                {item.data && <Text style={styles.legend_text}>{item.data}</Text>}
            </View>
        );
    }

    render(): JSX.Element {
        if (!this.props.data || !this.props.dataItems) {
            return <div>{i18n('Lade', 'sys')}...</div>;
        }

        return (
            <div
                style={{
                    borderTopRightRadius: 'var(--border-radius-headline)', borderTopLeftRadius: 'var(--border-radius-headline)',
                    boxShadow: 'var(--assistent-box-shadow)', display: 'flex', flexDirection: 'column'
                }}
            >
                <div
                    style={{
                        fontWeight: 600, fontSize: 'var(--font-size-headline)', backgroundColor: 'var(--color-bg-headline)',
                        maxHeight: 'var(--height-headline)', minHeight: 'var(--height-headline)', padding: '0 10px 0 20px',
                        borderTopRightRadius: 'var(--border-radius-headline)', borderTopLeftRadius: 'var(--border-radius-headline)',
                        display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'
                    }}
                >
                    {this.renderTitel()}
                </div>
                {this.renderDiagram()}
                {this.renderDiagram(true)}
            </div>
        );
    }

    renderDiagram(isPdf?: boolean): JSX.Element {
        return (
            <div
                style={{
                    flexDirection: 'row', display: 'flex', padding: '10px 0 5px 10px', boxSizing: 'border-box',
                    ...(isPdf ? { width: 450, height: 156, marginTop: -156, padding: 0, visibility: 'hidden' } : { height: 180/*184*/ })
                }}
            >
                <StackedDiagram
                    data={this.getData()}
                    bars={this.getBars()}
                    id={isPdf ? `energiedaten-${this.props.jahr || ''}` : null}
                    xAxisLabel={this.getAxisLabel()}
                />
            </div>
        );
    }

    renderHelpIcon(): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ display: 'flex', alignSelf: 'center' }}
                styleProps={{ margin: 0 }}
                label={i18n('Energie Cockpit - Visualisierung', 'sys')}
                helpKey='energie_cockpit_visualisierung'
                type='theme_info'
                style='theme_small'
                customElement='Info'
                editBar
                useTheme
            />
        );
    }

    renderTitel(): JSX.Element {
        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    {i18n('Energiedaten', 'sys')}<span style={{ fontSize: 'small', marginRight: 5, marginLeft: 3 }}>- {i18n('grafisch', 'sys')}</span>{this.renderHelpIcon()}
                </div>
                <div className={formStyle.formCompact}>
                    {/* <b style={{ color: '#888', fontSize: 11, marginRight: '0.5em' }}>{i18n('Auswahl', 'sys')}</b> */}
                    <select
                        style={{ width: 205 }}
                        value={this.state.selectedData}
                        onChange={(event: any) => this.setState({ selectedData: event.target.value })}
                    >
                        <option value={'v'}>{i18n('Energieverbrauch', 'sys')}</option>
                        {!this.disableKosten &&
                            <option value={'k'}>{i18n('Energiekosten', 'sys')}</option>
                        }
                        <option value={'c'}>{i18n('CO₂-Emission', 'sys')}</option>
                    </select>
                </div>
            </>
        );
    }
}
